<template>
    <div class="apprentice-simple-card">
        <div
            class="not-found with-header empty-paper-list"
            v-if="!itemsIn.length">
            <div class="icon">
                <i class="fas fa-exclamation-circle"></i>
            </div>
            <div class="alert-notice">{{ $t('group.no_papers_created_yet') }}</div>
        </div>

        <div v-else>
            <design-2021-apprentice-data-table
                :items="items"
                :fields="fields"
                :sort-compare="sortCompare">
                <template v-slot:options>
                    <b-button
                        variant="primary"
                        @click="showByStudents = !showByStudents">
                        <span v-if="showByStudents">{{ $t('group.list_by_paper') }}</span>
                        <span v-else>{{ $t('group.list_by_student') }}</span>
                    </b-button>
                </template>

                <template v-slot:cell(name)="data">
                    <div class="title-wrapper">
                        <b-button
                            class="btn-show btn-open open-project"
                            variant="primary-icon-only"
                            :href="viewRoute(data.item)">
                        </b-button>
                        <div>{{ data.item.name }}</div>
                    </div>
                </template>

                <template v-slot:cell(steps)="data">
                    <div class="progress-bar-wrapper">
                        <b-progress
                            class="portfolio-progress"
                            :max="data.item.max_steps"
                            show-value>
                            <b-progress-bar
                                v-for="(step, index) in data.item.steps"
                                v-b-tooltip.hover
                                :variant="getVariant(step)"
                                :value="step.value"
                                :key="index"
                                :title="toolTipLabel(step)">
                            </b-progress-bar>
                        </b-progress>
                    </div>
                </template>

                <template v-slot:cell(approvals)="data">
                    <div class="progress-bar-wrapper">
                        <b-progress
                            class="portfolio-progress"
                            :max="data.item.max_pages"
                            show-value>
                            <b-progress-bar
                                v-for="(page, index) in data.item.pages"
                                v-b-tooltip.hover
                                :variant="getVariant(page)"
                                :value="page.value"
                                :key="index"
                                :title="toolTipLabelPages(page)">
                            </b-progress-bar>
                        </b-progress>
                    </div>
                </template>

                <template v-slot:cell(last_save)="data">
                    {{ data.item.last_save | date_time_om }}
                </template>
            </design-2021-apprentice-data-table>
        </div>
    </div>
</template>

<script>
export default {
    name: 'group-progress-paper-component',
    props: ['itemsIn'],
    data() {
        return {
            showByStudents: true,
        };
    },
    computed: {
        fields() {
            let fields = [];

            fields.push({
                key: 'name',
                label: this.showByStudents ? this.$t('common.first_name') : this.$t('group.paper'),
                sortable: true,
            });

            if (this.showByStudents) {
                fields.push({
                    key: 'surname',
                    label: this.$t('common.last_name'),
                    sortable: true,
                });
            }

            if (this.itemsIn[0].roadmap_step_project_statuses !== undefined) {
                fields.push({
                    key: 'step_count',
                    label: this.$t('group.current_step'),
                    sortable: true,
                });
                fields.push({
                    key: 'steps',
                    label: this.$t('group.steps_status'),
                });
            }

            if (this.itemsIn[0].magazine !== undefined) {
                fields.push({
                    key: 'approvals',
                    label: this.$t('group.magazine_pages'),
                });

                fields.push({
                    key: 'last_save',
                    label: this.$t('group.magazine_last_saved'),
                    sortable: true,
                });
            }

            return fields;
        },
        items() {
            let array = [];

            for (const item of this.itemsIn) {
                //magazine pages
                let max_pages = item.magazine !== undefined ? item.magazine.num_pages : 0;
                let blue = 0;
                let yellow = 0;
                let green = 0;
                let last_save = '';

                if (item.magazine !== undefined) {
                    for (const page of item.magazine.pages_no_model) {
                        if (page.status === 'blue') blue++;
                        if (page.status === 'yellow') yellow++;
                        if (page.status === 'green') green++;
                        if (last_save === '') {
                            last_save = page.updated_at;
                        } else {
                            if (new Date(last_save) < new Date(page.updated_at)) {
                                last_save = page.updated_at;
                            }
                        }
                    }
                }

                const pages = {
                    0: {
                        key: 'done',
                        value: green,
                    },
                    2: {
                        key: 'submitted',
                        value: yellow,
                    },
                    3: {
                        key: 'open',
                        value: blue,
                    },
                };

                //roadmap steps
                let declined = 0;
                let done = 0;
                let submitted = 0;
                let open = 0;
                let missing_open = 0;

                if (item.roadmap_step_project_statuses !== undefined) {
                    for (const status of item.roadmap_step_project_statuses) {
                        if (status.status === 2 && status.finished === 0) {
                            //status 2 && finished 0 = open na heropening (rood)

                            declined++;
                        } else if (status.status === 1 && status.finished === 1) {
                            //status 1 en fisnihed 1 = goed gekeurd.

                            done++;
                        } else if (status.status === 0 && status.finished === 1) {
                            //status 0 en finished 1 = ingeleverd

                            submitted++;
                        } else {
                            //status 0 && finished 0 = open nog nooit ingeleverd.
                            open++;
                        }
                    }

                    missing_open = item.step_total - item.roadmap_step_project_statuses.length;
                }

                open += missing_open;

                const steps = {
                    0: {
                        key: 'done',
                        value: done,
                    },
                    1: {
                        key: 'declined',
                        value: declined,
                    },
                    2: {
                        key: 'submitted',
                        value: submitted,
                    },
                    3: {
                        key: 'open',
                        value: open,
                    },
                };

                if (this.showByStudents) {
                    for (const student of item.students) {
                        array.push({
                            id: item.id,
                            name: student.name,
                            surname: student.preposition
                                ? student.preposition + ' ' + student.surname
                                : student.surname,
                            max_steps: item.step_total,
                            max_pages: max_pages,
                            pages: pages,
                            last_save: last_save,
                            steps: steps,
                            step_count:
                                item.step_student !== item.step_total
                                    ? item.step_student + 1 + ' / ' + item.step_total
                                    : 'Klaar',
                        });
                    }
                } else {
                    array.push({
                        id: item.id,
                        name: item.title,
                        max_steps: item.step_total,
                        max_pages: max_pages,
                        pages: pages,
                        last_save: last_save,
                        steps: steps,
                        step_count:
                            item.step_student !== item.step_total
                                ? item.step_student + 1 + ' / ' + item.step_total
                                : 'Klaar',
                    });
                }
            }

            return array;
        },
    },
    methods: {
        sortCompare(firstRow, secondRow, key) {
            if (key === 'last_save') {
                return new Date(firstRow[key]) < new Date(secondRow[key]);
            } else {
                return false;
            }
        },

        toolTipLabel(step) {
            switch (step.key) {
                case 'done':
                    return `${step.value} ${this.$t('group.approved')}}`;
                case 'submitted':
                    return `${step.value} ${this.$t('group.submitted')}`;
                case 'open':
                    return `${step.value} ${this.$t('group.opened')}`;
                case 'declined':
                    return `${step.value} ${this.$t('group.declined')}`;
            }
        },

        toolTipLabelPages(page) {
            switch (page.key) {
                case 'done':
                    return `${page.value} ${this.$t('group.approved')}`;
                case 'submitted':
                    return `${page.value} ${this.$t('group.submitted')}`;
                case 'open':
                    return `${page.value} ${this.$t('common.open')}`;
            }
        },

        getVariant(step) {
            switch (step.key) {
                case 'done':
                    return 'success';
                case 'submitted':
                    return 'warning';
                case 'open':
                    return 'open';
                case 'declined':
                    return 'danger';
            }
        },

        viewRoute(item) {
            return this.routes.paperShow(item.id);
        },
    },
};
</script>

<style scoped lang="scss">
.open-project {
    padding-right: 5px;
}

.title-wrapper {
    display: flex;
    align-items: center;

    &:before {
        margin-right: map-get($grid-gutter-spacing, 'twenty-one');
    }
}

.portfolio-progress ::v-deep {
    height: 16px;
    border-radius: 0;

    .progress-bar {
        border-radius: 0;
    }
}
</style>
